import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Package,
  Send,
  Bell,
  User,
  Map,
  PlusCircle,
  Truck,
  Users,
  Route,
  MessageSquare,
  Wrench,
  LogOut,
  Menu,
  X,
  Shuffle,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import { clearImpersonationToken } from "@/lib/api";
import { ACCOUNT_LABELS } from "@/lib/format";

function navForRole(accountType) {
  if (accountType === "autista") {
    return [
      { to: "/", label: "Missioni", icon: Map, end: true },
      { to: "/notifications", label: "Notifiche", icon: Bell },
      { to: "/profile", label: "Profilo", icon: User },
    ];
  }
  if (accountType === "azienda") {
    return [
      { to: "/", label: "Home", icon: Home, end: true },
      { to: "/transports", label: "Trasporti", icon: Package },
      { to: "/missions", label: "Missioni", icon: Send },
      { to: "/notifications", label: "Notifiche", icon: Bell },
      { to: "/profile", label: "Profilo", icon: User },
    ];
  }
  // vettore (default)
  return [
    { to: "/", label: "Home", icon: Home, end: true },
    { to: "/transports", label: "Trasporti", icon: Package },
    { to: "/missions", label: "Missioni", icon: Send },
    { to: "/notifications", label: "Notifiche", icon: Bell },
    { to: "/profile", label: "Profilo", icon: User },
  ];
}

function secondaryLinks(accountType) {
  const common = [
    { to: "/trips", label: "Cerca viaggi", icon: Map },
    { to: "/free-vehicles", label: "Mezzi liberi", icon: Truck },
    { to: "/chats", label: "Chat", icon: MessageSquare },
    { to: "/services", label: "Borsa Servizi", icon: Wrench },
  ];
  if (accountType === "azienda") {
    return [
      { to: "/publish-trip", label: "Pubblica viaggio", icon: PlusCircle },
      { to: "/ripartizione", label: "Ripartizione viaggi", icon: Shuffle },
      { to: "/carriers", label: "I miei vettori", icon: Users },
      ...common,
    ];
  }
  if (accountType === "vettore") {
    return [
      { to: "/publish-trip", label: "Pubblica viaggio", icon: PlusCircle },
      { to: "/carrier-routes", label: "Dove cerco carichi", icon: Route },
      { to: "/drivers", label: "Autisti", icon: Users },
      { to: "/fleet", label: "La mia flotta", icon: Truck },
      ...common,
    ];
  }
  return [];
}

function isActive(pathname, to, end) {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/");
}

function BrandLogo({ className }) {
  return (
    <img
      src="/mycargoflow-logo-wide.png"
      alt="MyCargoFlow"
      className={cn("object-contain", className)}
    />
  );
}

export function AppShell({ children }) {
  const { user, logout, refresh } = useAuth();
  const { unreadCount } = useNotifications();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const accountType = user?.account_type;
  const nav = navForRole(accountType);
  const secondary = secondaryLinks(accountType);

  const roleLabel =
    accountType === "vettore" ? "Vettore" : accountType === "autista" ? "Autista" : "Azienda";

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const exitImpersonation = async () => {
    clearImpersonationToken();
    await refresh();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {user?.is_impersonated ? (
        <div
          data-testid="impersonation-banner"
          className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-bold text-amber-950"
        >
          <span className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            Modalità Admin · stai visualizzando come <strong>{user?.company_name}</strong> ({roleLabel})
          </span>
          <button
            data-testid="impersonation-exit"
            onClick={exitImpersonation}
            className="shrink-0 rounded-lg bg-amber-950 px-3 py-1.5 text-xs font-extrabold text-amber-50 hover:opacity-90"
          >
            Esci dalla modalità
          </button>
        </div>
      ) : null}
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card/70 backdrop-blur">
        <div className="px-4 py-5">
          <BrandLogo className="h-auto w-full" />
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {nav.map((item) => {
            const active = isActive(pathname, item.to, item.end);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors",
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="flex-1">{item.label}</span>
                {item.to === "/notifications" && unreadCount > 0 ? (
                  <span data-testid="nav-notifications-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-extrabold text-primary-foreground">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                ) : null}
              </Link>
            );
          })}

          {secondary.length > 0 ? (
            <>
              <p className="px-3 pt-5 pb-2 text-[10px] font-extrabold tracking-widest text-muted-foreground">
                SCORCIATOIE
              </p>
              {secondary.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                    isActive(pathname, item.to, false)
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-secondary/70"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </>
          ) : null}

        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 px-2">
            <p className="text-[10px] font-extrabold tracking-widest text-primary">{roleLabel.toUpperCase()}</p>
            <p className="truncate text-sm font-bold text-foreground">{user?.company_name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout"
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/10"
          >
            <LogOut className="h-5 w-5" /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-border bg-card/80 px-3 py-1.5 backdrop-blur">
        <div className="min-w-0 flex-1 pr-5">
          <BrandLogo className="h-auto w-full max-h-[128px] object-left" />
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="shrink-0 rounded-lg p-2 text-foreground hover:bg-secondary"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute right-0 top-0 h-full w-72 overflow-y-auto bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1 pr-2">
                <BrandLogo className="h-auto w-full max-h-[88px] object-left" />
              </div>
              <button onClick={() => setMobileOpen(false)} className="shrink-0 rounded-lg p-2 hover:bg-secondary">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-1">
              {[...nav, ...secondary].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold",
                    isActive(pathname, item.to, item.end)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-secondary"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.to === "/notifications" && unreadCount > 0 ? (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-extrabold text-primary-foreground">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  ) : null}
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/10"
              >
                <LogOut className="h-5 w-5" /> Esci
              </button>
            </nav>
          </div>
        </div>
      ) : null}

      {/* Content */}
      <main className="lg:pl-64">
        <div className="mx-auto w-full max-w-4xl px-4 py-6 pb-28 lg:pb-10 sm:px-6">{children}</div>
      </main>

      {/* Mobile floating bottom nav (glass pill) */}
      <nav className="lg:hidden fixed inset-x-3 bottom-3 z-30 flex h-16 items-center justify-around rounded-[31px] border border-border bg-white/80 shadow-lg backdrop-blur-xl">
        {nav.map((item) => {
          const active = isActive(pathname, item.to, item.end);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`bottomnav-${item.label.toLowerCase()}`}
              className="relative flex flex-1 flex-col items-center justify-center gap-0.5"
            >
              {item.to === "/notifications" && unreadCount > 0 ? (
                <span data-testid="bottomnav-notifications-badge" className="absolute top-1 right-[22%] flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-primary-foreground">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
              <item.icon className={cn("h-6 w-6", active ? "text-primary" : "text-muted-foreground")} />
              <span
                className={cn(
                  "text-[10px] font-semibold",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
